/**
 * Fehler in Klartext übersetzen.
 *
 * Warum es diese Datei gibt: Die Datenbank antwortet mit Sätzen wie
 * "new row violates row-level security policy for table \"mitarbeiter\"".
 * Wer im Büro sitzt, liest daraus, dass etwas kaputt ist — dabei fehlt
 * meistens nur ein Recht oder es hat jemand anderes schneller gespeichert.
 * Eine Meldung, die niemand versteht, führt dazu, dass man es einfach noch
 * einmal versucht, und beim zweiten Mal geht dann wirklich etwas schief.
 *
 * Die Regel dabei: NIE beschönigen. Wenn etwas nicht gespeichert wurde, muss
 * das dastehen. Übersetzt wird das Warum, nicht das Ob.
 */

export type Fehlerart =
  | 'kein_recht'
  | 'gleichzeitig_geaendert'
  | 'doppelt'
  | 'netz'
  | 'anmeldung'
  | 'regel'
  | 'unbekannt';

export type Klarfehler = {
  art: Fehlerart;
  titel: string;
  text: string;
  /** Der Originaltext — für die Zeile, die man beim Nachfragen weitergibt. */
  original: string;
};

/**
 * Postgres-Fehlercodes, die hier vorkommen können. Der Rest landet bewusst
 * in 'unbekannt' und zeigt den Originaltext an, statt ihn zu verschlucken.
 */
const NACH_CODE: Record<string, { art: Fehlerart; titel: string; text: string }> = {
  '42501': {
    art: 'kein_recht',
    titel: 'Dafür fehlt die Berechtigung',
    text: 'Dein Konto darf das nicht ändern. Wenn das falsch ist, muss die Rolle im Backoffice angepasst werden.',
  },
  '23505': {
    art: 'doppelt',
    titel: 'Das gibt es schon',
    text: 'Ein Eintrag mit diesen Angaben ist bereits vorhanden. Bitte suche ihn, statt einen zweiten anzulegen.',
  },
  '23503': {
    art: 'regel',
    titel: 'Der Eintrag hängt noch an etwas',
    text: 'Es gibt noch Daten, die auf diesen Eintrag verweisen. Er lässt sich deshalb nicht entfernen.',
  },
  '23514': {
    art: 'regel',
    titel: 'Die Angaben passen nicht zusammen',
    text: 'Die Datenbank hat eine Regel abgelehnt. Bitte prüfe die eingetragenen Werte.',
  },
  P0001: {
    art: 'regel',
    titel: 'Das ist so nicht vorgesehen',
    text: 'Die Datenbank hat den Vorgang mit einer eigenen Regel gestoppt.',
  },
};

export function alsKlarfehler(fehler: unknown): Klarfehler {
  const roh = fehler as
    | { code?: string; message?: string; details?: string; hint?: string }
    | undefined;
  const original = roh?.message ?? String(fehler);

  // Kein Netz oder Supabase nicht erreichbar. Das ist der Fall, in dem der
  // Hinweis "später noch einmal" wirklich stimmt — bei allen anderen wäre er
  // eine Ausrede.
  if (
    original.includes('Failed to fetch') ||
    original.includes('NetworkError') ||
    original.includes('fetch failed')
  ) {
    return {
      art: 'netz',
      titel: 'Keine Verbindung',
      text: 'Der Server ist gerade nicht erreichbar. Nichts wurde gespeichert. Prüfe die Internetverbindung und versuche es noch einmal.',
      original,
    };
  }

  if (
    original.includes('JWT') ||
    original.includes('Invalid login') ||
    original.includes('refresh_token')
  ) {
    return {
      art: 'anmeldung',
      titel: 'Anmeldung abgelaufen',
      text: 'Bitte melde dich neu an.',
      original,
    };
  }

  const bekannt = roh?.code ? NACH_CODE[roh.code] : undefined;
  if (bekannt) {
    // Bei einer eigenen Datenbankregel (P0001) steht der eigentliche Grund im
    // Text — den zu verstecken wäre der schlimmere Fehler.
    const text =
      roh?.code === 'P0001' ? original : (roh?.details ?? bekannt.text);
    return { art: bekannt.art, titel: bekannt.titel, text, original };
  }

  return {
    art: 'unbekannt',
    titel: 'Das hat nicht geklappt',
    text: original,
    original,
  };
}

/**
 * Der Fall, für den es keinen Datenbankcode gibt: Zwei Personen hatten
 * dieselbe Zeile offen, eine hat gespeichert, die andere sieht noch den alten
 * Stand. Wir prüfen das über `geaendert_am` (siehe speichern.ts) und werfen
 * dann diesen Fehler — bewusst mit dem Hinweis, was zu tun ist, denn die
 * Antwort ist nie "nochmal drücken", sondern "erst schauen, was der andere
 * geändert hat".
 */
export function gleichzeitigGeaendert(
  was: string,
  /**
   * Was danach zu tun ist. Bildschirme, die nach dem Konflikt selbst
   * auffrischen und die Eingabe behalten, dürfen NICHT „Lade die Seite neu"
   * sagen — wer das befolgt, verliert genau die Eingabe, die erhalten blieb.
   */
  danach = 'Lade die Seite neu, schau dir den neuen Stand an und trage deine Änderung dann noch einmal ein.',
): Klarfehler {
  return {
    art: 'gleichzeitig_geaendert',
    titel: 'Jemand war schneller',
    text: `${was} wurde inzwischen von jemand anderem geändert. Es wurde NICHTS gespeichert. ${danach}`,
    original: 'geaendert_am stimmt nicht mehr mit dem gelesenen Stand überein',
  };
}
